import type { UploadedDocumentationPayload } from 'shared/domain/documentation/documentationContract';

/** What a channel message means for the documentation view. */
export enum DocumentationChangeKind {
  /** Delete or move - confirm optimistic removals. */
  removed = 'removed',
  /** Create, update, upload, restore or batch create - refresh data. */
  changed = 'changed',
}

export type { UploadedDocumentationPayload };

/** Result of `resolveDocumentationRefresh`. */
export interface DocumentationRefreshTarget {
  directoryId: string;
  /**
   * The store left the folder that owns the open preview (edit/save can briefly
   * force root) - navigate back instead of refreshing another folder.
   */
  restoreDirectory: boolean;
}

/**
 * Reactions to service worker broadcasts. Debounced here so bursts of messages
 * (bulk upload, archive) cause one refresh.
 */
export interface ChannelUpdatesSliceActions {
  handleDocumentationChange: (kind: DocumentationChangeKind) => void;
  handleDirectoryChange: (kind: DocumentationChangeKind) => void;
  handleDocumentationUploaded: (payload: UploadedDocumentationPayload) => void;
  /** Cancels pending debounced refreshes (store reset, project change). */
  cancelPendingChannelRefreshes: () => void;
  /**
   * Subscribes the store to the documentation and directory events of the
   * service worker. Returns the unsubscribe; safe to call more than
   * once (each call is an independent subscription, reactions are debounced or
   * idempotent).
   */
  subscribeToChannelMessages: () => () => void;
}

export type ChannelUpdatesSlice = ChannelUpdatesSliceActions;
